<script setup lang="ts">
/**
 * CampaignCountdown — barre de temps ANIMÉE d'une cagnotte qui a une date de
 * fin : « ● Il reste 9 jours ……… fin le 8 octobre » au-dessus d'une piste
 * fine qui se remplit avec le temps écoulé. Incite à donner sans crier
 * (Karim 2026-09-30 : « une progress animée plutôt »).
 *
 * - Le remplissage s'anime à l'arrivée (de 0 à sa valeur), puis suit le
 *   temps ; seul le point pulse (Karim 2026-09-30 : pas de reflet continu).
 * - Le temps écoulé se mesure entre le lancement de la cagnotte
 *   (`startsAt` : date de début, sinon date de création) et sa clôture
 *   (`closesAt`, minuit à la fin du jour de fin, heure de l'association —
 *   calculé par l'API). Sans date de lancement connue, on mesure sur les
 *   30 derniers jours avant la clôture : le sens (« ça avance ») est gardé.
 * - Trois teintes : bleu Donoor (plus de 7 jours), ambre (7 jours ou moins),
 *   rouge le dernier jour — où le texte passe aux heures et minutes.
 * - Animations coupées si le visiteur préfère moins de mouvement.
 *
 * Composant PARTAGÉ (@donoor/ui), couleurs en dur comme les autres
 * composants de la page cagnotte.
 *
 * Internationalisation (lot "paquets partagés", 2026-10-11) : textes dans le
 * domaine `ui` (clés `ui.countdown.*`), présent dans donoor.org ET
 * dashboard.donoor.org — `useI18n()` est auto-importé par les 2 apps.
 */
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    /** Instant de clôture, ISO 8601 (« 2026-10-08T22:00:00.000Z »). */
    closesAt: string;
    /** Lancement de la cagnotte (ISO), pour mesurer le temps écoulé. */
    startsAt?: string | null;
    /** « 8 octobre » — le jour de fin, en clair. */
    endDateLabel: string;
    /** Jours restants, calculés par l'API dans le fuseau de l'association. */
    daysRemaining?: number | null;
    /** Version resserrée (mobile). */
    compact?: boolean;
  }>(),
  { startsAt: null, daysRemaining: null, compact: false },
);

const emit = defineEmits<{
  /** Le temps est écoulé : la cagnotte vient de se clôturer. */
  ended: [];
}>();

const FENETRE_PAR_DEFAUT_MS = 30 * 86_400_000;

// L'horloge ne démarre qu'au montage : rendu serveur et premier rendu client
// identiques (pas de décalage d'hydratation).
const monte = ref(false);
const maintenant = ref(0);
/** Largeur affichée : posée après le montage pour que la barre s'anime. */
const largeurAffichee = ref(0);
/**
 * Animation de la largeur seulement à l'ARRIVÉE. Ensuite, chaque seconde ne
 * déplace la barre que d'une fraction de pixel : sans transition. Avant, la
 * transition (1,4 s) durait plus que l'intervalle (1 s) : la barre animait sa
 * largeur EN CONTINU, et la page entière recalculait sa mise en page à chaque
 * image (saccades sur mobile, 2026-10-13).
 */
const arrivee = ref(true);
let horloge: ReturnType<typeof setInterval> | null = null;
let finArrivee: ReturnType<typeof setTimeout> | null = null;

const cloture = computed(() => new Date(props.closesAt).getTime());

const lancement = computed(() => {
  const brut = props.startsAt ? new Date(props.startsAt).getTime() : Number.NaN;
  if (Number.isFinite(brut) && brut < cloture.value) return brut;
  return cloture.value - FENETRE_PAR_DEFAUT_MS;
});

const resteMs = computed(() =>
  monte.value ? Math.max(0, cloture.value - maintenant.value) : 0,
);

const termine = computed(() => monte.value && resteMs.value <= 0);

/** Part du temps écoulée, entre 2 % (on voit la barre) et 100 %. */
const progression = computed(() => {
  if (!monte.value) return 0;
  const total = cloture.value - lancement.value;
  if (total <= 0) return 100;
  const ecoule = maintenant.value - lancement.value;
  return Math.min(100, Math.max(2, (ecoule / total) * 100));
});

onMounted(() => {
  maintenant.value = Date.now();
  monte.value = true;
  // Un tour de rendu à 0, puis la vraie valeur : la transition CSS fait
  // grandir la barre.
  requestAnimationFrame(() => {
    largeurAffichee.value = progression.value;
  });
  finArrivee = setTimeout(() => (arrivee.value = false), 1500);
  horloge = setInterval(() => {
    maintenant.value = Date.now();
    largeurAffichee.value = progression.value;
  }, 1000);
});

onUnmounted(() => {
  if (horloge) clearInterval(horloge);
  if (finArrivee) clearTimeout(finArrivee);
});

watch(termine, (fini) => {
  if (fini) emit("ended");
});

/** Ambiance : calme (> 7 j), proche (≤ 7 j), urgente (dernier jour). */
const niveau = computed<"calme" | "proche" | "urgent">(() => {
  if (!monte.value) return "calme";
  const jours = resteMs.value / 86_400_000;
  if (jours <= 1) return "urgent";
  if (jours <= 7) return "proche";
  return "calme";
});

/**
 * « Il reste 9 jours » (compte de l'API, exact dans le fuseau de
 * l'association) ; le dernier jour, les heures et minutes en direct.
 */
const texteReste = computed(() => {
  if (termine.value) return t("ui.countdown.termine");
  const s = Math.floor(resteMs.value / 1000);
  if (s < 86_400) {
    const heures = Math.floor(s / 3_600);
    const minutes = Math.floor((s % 3_600) / 60);
    if (heures === 0) return t("ui.countdown.plusQueMinutes", { minutes });
    return t("ui.countdown.plusQueHeuresMinutes", {
      heures,
      minutes: String(minutes).padStart(2, "0"),
    });
  }
  const jours = props.daysRemaining ?? Math.ceil(s / 86_400);
  return jours <= 1 ? t("ui.countdown.dernierJour") : t("ui.countdown.ilResteJours", { jours });
});

/** Phrase complète pour les lecteurs d'écran. */
const phrase = computed(() =>
  termine.value
    ? t("ui.countdown.ariaTerminee")
    : t("ui.countdown.ariaPhrase", {
        texteReste: texteReste.value,
        endDateLabel: props.endDateLabel,
      }),
);
</script>

<template>
  <div
    class="tp"
    :class="[`tp--${niveau}`, { 'tp--compact': compact, 'tp--fini': termine }]"
    role="group"
    :aria-label="phrase"
  >
    <div class="tp__ligne">
      <span class="tp__reste">
        <span class="tp__point" aria-hidden="true" />
        <span>{{ texteReste }}</span>
      </span>
      <span v-if="!termine" class="tp__fin">{{ t("ui.countdown.finLe", { endDateLabel }) }}</span>
    </div>
    <div class="tp__piste" aria-hidden="true">
      <div
        class="tp__remplissage"
        :class="{ 'tp__remplissage--arrivee': arrivee }"
        :style="{ width: `${largeurAffichee}%` }"
      />
    </div>
  </div>
</template>

<style scoped>
.tp {
  --tp-texte: #0c3097;
  --tp-doux: #5b6b8f;
  --tp-piste: #e6eefb;
  /* Bleu du bouton « Je participe » (CampaignMobileActions). */
  --tp-fin: #0f3d8c;
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: var(--tp-texte);
}

.tp--proche {
  --tp-texte: #8a4300;
  --tp-doux: #a6702e;
  --tp-piste: #fdeedb;
  --tp-fin: #ff9500;
}

.tp--urgent {
  --tp-texte: #b91c1c;
  --tp-doux: #c05c5c;
  --tp-piste: #fde3e3;
  --tp-fin: #ff3b30;
}

.tp--fini {
  --tp-texte: #4b5563;
  --tp-doux: #6b7280;
  --tp-piste: #e5e7eb;
  --tp-fin: #6b7280;
}

.tp__ligne {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  line-height: 18px;
}

.tp__reste {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  white-space: nowrap;
}

.tp__point {
  position: relative;
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  border-radius: 9999px;
  background: var(--tp-fin);
}
/* Onde qui s'élargit et s'efface : transform + opacity seulement (la carte
   graphique l'anime seule ; une ombre animée repeignait à chaque image). */
.tp__point::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--tp-fin) 45%, transparent);
  animation: tp-pulse 1.8s ease-out infinite;
  will-change: transform, opacity;
}

.tp__fin {
  font-weight: 500;
  color: var(--tp-doux);
  white-space: nowrap;
}

.tp__piste {
  position: relative;
  /* 10 px, compact compris : à 5-6 px, la barre était trop fine à côté des
     boutons « Je participe / Partager » — un trait plutôt qu'une jauge, qui
     donnait même l'impression de dépasser leur largeur (Karim 2026-10-09). */
  height: 10px;
  overflow: hidden;
  border-radius: 9999px;
  background: var(--tp-piste);
}

.tp__remplissage {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 9999px;
  /* Couleur PLEINE, sans dégradé (Karim 2026-10-09) : même bleu que le
     bouton « Je participe » ; orange / rouge / gris selon l'échéance. */
  background: var(--tp-fin);
}
/* Grandit à l'arrivée seulement ; ensuite il suit le temps sans animation. */
.tp__remplissage--arrivee {
  transition: width 1.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.tp--compact .tp__ligne {
  font-size: 12px;
}

@keyframes tp-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  70% {
    transform: scale(3);
    opacity: 0;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tp__point::after {
    animation: none;
    display: none;
  }

  .tp__remplissage--arrivee {
    transition: none;
  }
}
</style>
